import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Save as SaveIcon,
  Receipt as ReceiptIcon,
  Bluetooth as BluetoothIcon,
  Wifi as WifiIcon,
  Inventory2 as DrawerIcon,
} from '@mui/icons-material';
import { Switch, FormControlLabel, Divider } from '@mui/material';
import { collection, getDocs, query, where, orderBy, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Printer, PrinterConnectionType, PrinterModel } from '../types';

const Printers: React.FC = () => {
  const { shop } = useAuth();
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<Printer | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Printer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testingPrinterId, setTestingPrinterId] = useState<string | null>(null);
  const [receiptPrinterId, setReceiptPrinterId] = useState<string>('');
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [receiptSettings, setReceiptSettings] = useState({
    headerMessage: '',
    footerMessage: 'ありがとうございました',
    showShopAddress: true,
    showShopPhone: true,
    showTaxDetails: true,
    showRegistrationNumber: '',
    logoUrl: '',
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'kitchen' as 'kitchen' | 'bar' | 'receipt' | 'other',
    connectionType: 'network' as PrinterConnectionType,
    ipAddress: '',
    port: 9100,
    bluetoothAddress: '',
    bluetoothName: '',
    model: 'SII-RP-F10' as PrinterModel | string,
    hasDrawer: false,
    isActive: true,
  });

  useEffect(() => {
    if (shop) {
      fetchPrinters();
      setReceiptPrinterId(shop.receiptPrinterId || '');
      // レシート設定の読み込み
      if (shop.receiptSettings) {
        setReceiptSettings({
          headerMessage: shop.receiptSettings.headerMessage || '',
          footerMessage: shop.receiptSettings.footerMessage || 'ありがとうございました',
          showShopAddress: shop.receiptSettings.showShopAddress ?? true,
          showShopPhone: shop.receiptSettings.showShopPhone ?? true,
          showTaxDetails: shop.receiptSettings.showTaxDetails ?? true,
          showRegistrationNumber: shop.receiptSettings.showRegistrationNumber || '',
          logoUrl: shop.receiptSettings.logoUrl || '',
        });
      }
    }
  }, [shop]);

  const fetchPrinters = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'printers'),
        where('shopId', '==', shop.id)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Printer[];
      setPrinters(data);
    } catch (error) {
      console.error('プリンター取得エラー:', error);
      setError('プリンターの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReceiptSettings = async () => {
    if (!shop) {
      console.error('Shop is null, cannot save receipt settings');
      return;
    }

    setSavingReceipt(true);
    setError(null);

    try {
      const shopRef = doc(db, 'shops', shop.id);
      const updateData = {
        receiptPrinterId: receiptPrinterId || null,
        receiptSettings: receiptSettings,
        updatedAt: Timestamp.now(),
      };

      await updateDoc(shopRef, updateData);

      setSuccess('レシート設定を保存しました');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('レシート設定保存エラー:', err);
      setError('レシート設定の保存に失敗しました');
    } finally {
      setSavingReceipt(false);
    }
  };

  // ロゴ画像アップロード
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !shop) return;

    // ファイルサイズチェック（500KB以下）
    if (file.size > 500 * 1024) {
      setError('ロゴ画像は500KB以下にしてください');
      return;
    }

    // ファイル形式チェック
    if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
      setError('PNG、JPEG、GIF形式のみ対応しています');
      return;
    }

    setUploadingLogo(true);
    setError(null);

    try {
      // 既存のロゴがあれば削除
      if (receiptSettings.logoUrl) {
        try {
          const oldRef = ref(storage, `shops/${shop.id}/receipt-logo`);
          await deleteObject(oldRef);
        } catch (e) {
          // 古いファイルがなくてもエラーにしない
        }
      }

      // 新しいロゴをアップロード
      const storageRef = ref(storage, `shops/${shop.id}/receipt-logo`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      setReceiptSettings({ ...receiptSettings, logoUrl: downloadUrl });
      setSuccess('ロゴ画像をアップロードしました。保存ボタンを押して設定を保存してください。');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('ロゴアップロードエラー:', err);
      setError('ロゴ画像のアップロードに失敗しました');
    } finally {
      setUploadingLogo(false);
    }
  };

  // ロゴ画像削除
  const handleLogoDelete = async () => {
    if (!shop || !receiptSettings.logoUrl) return;

    try {
      const storageRef = ref(storage, `shops/${shop.id}/receipt-logo`);
      await deleteObject(storageRef);
      setReceiptSettings({ ...receiptSettings, logoUrl: '' });
      setSuccess('ロゴ画像を削除しました。保存ボタンを押して設定を保存してください。');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('ロゴ削除エラー:', err);
      // ファイルがなくてもURLはクリアする
      setReceiptSettings({ ...receiptSettings, logoUrl: '' });
    }
  };

  const handleOpenDialog = (printer?: Printer) => {
    if (printer) {
      setEditingPrinter(printer);
      setFormData({
        name: printer.name,
        type: printer.type,
        connectionType: printer.connectionType || 'network',
        ipAddress: printer.ipAddress || '',
        port: printer.port || 9100,
        bluetoothAddress: printer.bluetoothAddress || '',
        bluetoothName: printer.bluetoothName || '',
        model: printer.model,
        hasDrawer: printer.hasDrawer || false,
        isActive: printer.isActive,
      });
    } else {
      setEditingPrinter(null);
      setFormData({
        name: '',
        type: 'kitchen',
        connectionType: 'network',
        ipAddress: '',
        port: 9100,
        bluetoothAddress: '',
        bluetoothName: '',
        model: 'SII-RP-F10',
        hasDrawer: false,
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPrinter(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!shop || !formData.name) {
      setError('プリンター名は必須です');
      return;
    }

    // ネットワーク接続の場合はIPアドレスを検証
    if (formData.connectionType === 'network') {
      if (!formData.ipAddress) {
        setError('IPアドレスは必須です');
        return;
      }
      const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
      if (!ipRegex.test(formData.ipAddress)) {
        setError('正しいIPアドレスを入力してください（例: 192.168.1.100）');
        return;
      }
    }

    // Bluetooth接続の場合はデバイス名またはアドレスを検証
    if (formData.connectionType === 'bluetooth') {
      if (!formData.bluetoothAddress && !formData.bluetoothName) {
        setError('Bluetoothデバイス名またはアドレスを入力してください');
        return;
      }
    }

    try {
      const now = Timestamp.now();
      const printerData: any = {
        shopId: shop.id,
        name: formData.name,
        type: formData.type,
        connectionType: formData.connectionType,
        model: formData.model,
        hasDrawer: formData.hasDrawer,
        isActive: formData.isActive,
        updatedAt: now,
      };

      // 接続タイプに応じたフィールドを追加
      if (formData.connectionType === 'network') {
        printerData.ipAddress = formData.ipAddress;
        printerData.port = Number(formData.port);
        // Bluetoothフィールドをクリア
        printerData.bluetoothAddress = null;
        printerData.bluetoothName = null;
      } else {
        printerData.bluetoothAddress = formData.bluetoothAddress || null;
        printerData.bluetoothName = formData.bluetoothName || null;
        // ネットワークフィールドをクリア
        printerData.ipAddress = null;
        printerData.port = null;
      }

      if (editingPrinter) {
        // 更新
        await updateDoc(doc(db, 'printers', editingPrinter.id), printerData);
        setSuccess('プリンターを更新しました');
      } else {
        // 新規作成
        await addDoc(collection(db, 'printers'), {
          ...printerData,
          createdAt: now,
        });
        setSuccess('プリンターを追加しました');
      }

      handleCloseDialog();
      fetchPrinters();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('プリンター保存エラー:', error);
      setError('プリンターの保存に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDoc(doc(db, 'printers', deleteTarget.id));
      setSuccess('プリンターを削除しました');
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      fetchPrinters();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('プリンター削除エラー:', error);
      setError('プリンターの削除に失敗しました');
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      kitchen: 'キッチン',
      bar: 'バー',
      receipt: 'レシート',
      other: 'その他',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'default'> = {
      kitchen: 'primary',
      bar: 'secondary',
      receipt: 'success',
      other: 'default',
    };
    return colors[type] || 'default';
  };

  const handleTestPrint = async (printer: Printer) => {
    setTestingPrinterId(printer.id);
    setError(null);

    try {
      // まずCloud Functionを試す
      let response: Response | null = null;
      try {
        response = await fetch('https://asia-northeast1-reservation-platform-prod.cloudfunctions.net/testPrint', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            printerId: printer.id,
            ipAddress: printer.ipAddress,
            port: printer.port,
            printerName: printer.name,
          }),
        });
      } catch (cloudError) {
        console.warn('Cloud Function failed, trying local server:', cloudError);

        // Cloud Functionが失敗した場合、ローカルプリンターサーバーを試す
        try {
          response = await fetch('http://localhost:3100/api/print/test', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ipAddress: printer.ipAddress,
              port: printer.port,
              printerName: printer.name,
            }),
          });
        } catch (localError) {
          console.warn('Local server also failed:', localError);
          response = null;
        }
      }

      // レスポンスがnullの場合（両方失敗）
      if (!response) {
        throw new Error('プリンターサーバーに接続できませんでした。ローカルプリンターサーバーが起動しているか確認してください。');
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`テスト印刷に失敗しました: ${errorText}`);
      }

      const result = await response.json();

      if (result.success) {
        setSuccess(`${printer.name}にテスト印刷を送信しました`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(result.error || 'テスト印刷に失敗しました');
      }
    } catch (error: any) {
      console.error('テスト印刷エラー:', error);
      setError(`テスト印刷に失敗しました: ${error.message}`);
    } finally {
      setTestingPrinterId(null);
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">プリンター設定</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          プリンター追加
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* レシート設定 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ReceiptIcon color="primary" />
          <Typography variant="h6">
            レシート設定
          </Typography>
        </Box>

        {/* プリンター選択 */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
          レシートプリンター
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          会計時にレシートを印刷するプリンターを選択してください
        </Typography>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>レシートプリンター</InputLabel>
          <Select
            value={receiptPrinterId}
            label="レシートプリンター"
            onChange={(e) => setReceiptPrinterId(e.target.value)}
          >
            <MenuItem value="">なし（印刷しない）</MenuItem>
            {printers.map((printer) => (
              <MenuItem key={printer.id} value={printer.id}>
                {printer.name} ({printer.ipAddress}:{printer.port})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        {/* レシート印刷内容 */}
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
          レシート印刷内容
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          会計時に印刷されるレシートの内容をカスタマイズできます
        </Typography>

        <Grid container spacing={2}>
          {/* ロゴ画像 */}
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              ロゴ画像
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {receiptSettings.logoUrl ? (
                <Box sx={{
                  width: 120,
                  height: 60,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  backgroundColor: '#fafafa'
                }}>
                  <img
                    src={receiptSettings.logoUrl}
                    alt="レシートロゴ"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </Box>
              ) : (
                <Box sx={{
                  width: 120,
                  height: 60,
                  border: '1px dashed #ccc',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fafafa'
                }}>
                  <Typography variant="caption" color="text.secondary">
                    未設定
                  </Typography>
                </Box>
              )}
              <Box>
                <input
                  accept="image/png,image/jpeg,image/gif"
                  style={{ display: 'none' }}
                  id="logo-upload"
                  type="file"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                />
                <label htmlFor="logo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    size="small"
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? 'アップロード中...' : 'ロゴをアップロード'}
                  </Button>
                </label>
                {receiptSettings.logoUrl && (
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    onClick={handleLogoDelete}
                    sx={{ ml: 1 }}
                  >
                    削除
                  </Button>
                )}
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              PNG/JPEG/GIF形式、500KB以下。レシート上部に印刷されます。
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="ヘッダーメッセージ"
              value={receiptSettings.headerMessage}
              onChange={(e) => setReceiptSettings({ ...receiptSettings, headerMessage: e.target.value })}
              placeholder="例: ご来店ありがとうございます"
              helperText="レシート上部（店舗名の下）に表示されるメッセージ"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="フッターメッセージ"
              value={receiptSettings.footerMessage}
              onChange={(e) => setReceiptSettings({ ...receiptSettings, footerMessage: e.target.value })}
              placeholder="例: またのご来店をお待ちしております"
              helperText="レシート下部に表示されるメッセージ"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="インボイス登録番号"
              value={receiptSettings.showRegistrationNumber}
              onChange={(e) => setReceiptSettings({ ...receiptSettings, showRegistrationNumber: e.target.value })}
              placeholder="例: T1234567890123"
              helperText="適格請求書発行事業者登録番号（任意）"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            表示項目
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={receiptSettings.showShopAddress}
                  onChange={(e) => setReceiptSettings({ ...receiptSettings, showShopAddress: e.target.checked })}
                />
              }
              label="店舗住所を表示"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={receiptSettings.showShopPhone}
                  onChange={(e) => setReceiptSettings({ ...receiptSettings, showShopPhone: e.target.checked })}
                />
              }
              label="店舗電話番号を表示"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={receiptSettings.showTaxDetails}
                  onChange={(e) => setReceiptSettings({ ...receiptSettings, showTaxDetails: e.target.checked })}
                />
              }
              label="税額の内訳を表示"
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveReceiptSettings}
            disabled={savingReceipt}
          >
            {savingReceipt ? '保存中...' : 'レシート設定を保存'}
          </Button>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          <strong>対応プリンター:</strong>
        </Typography>
        <Typography variant="caption" display="block">
          • <strong>ネットワーク接続:</strong> SII製（RP-F10, RP-E11等）、Star TSP100、EPSON TM-T88等
        </Typography>
        <Typography variant="caption" display="block">
          • <strong>Bluetooth接続:</strong> Star mPOP（ドロワー内蔵）、Star TSP100BT等
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          ※ Bluetooth接続はスタッフアプリ（iOS/Android）でのみ使用できます
        </Typography>
        <Typography variant="caption" display="block">
          ※ ダッシュボード（Web）からのテスト印刷はネットワーク接続プリンターのみ対応
        </Typography>
      </Alert>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>プリンター名</TableCell>
                <TableCell>タイプ</TableCell>
                <TableCell>接続</TableCell>
                <TableCell>機種</TableCell>
                <TableCell>接続先</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {printers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    プリンターが登録されていません
                  </TableCell>
                </TableRow>
              ) : (
                printers.map((printer) => (
                  <TableRow key={printer.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PrintIcon color="action" />
                        {printer.name}
                        {printer.hasDrawer && (
                          <DrawerIcon fontSize="small" color="action" titleAccess="ドロワー内蔵" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTypeLabel(printer.type)}
                        color={getTypeColor(printer.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={printer.connectionType === 'bluetooth' ? <BluetoothIcon /> : <WifiIcon />}
                        label={printer.connectionType === 'bluetooth' ? 'BT' : 'LAN'}
                        size="small"
                        variant="outlined"
                        color={printer.connectionType === 'bluetooth' ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{printer.model}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {printer.connectionType === 'bluetooth'
                          ? printer.bluetoothName || printer.bluetoothAddress || '-'
                          : `${printer.ipAddress || '-'}:${printer.port || 9100}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={printer.isActive ? '有効' : '無効'}
                        color={printer.isActive ? 'success' : 'default'}
                        size="small"
                        icon={printer.isActive ? <CheckCircleIcon /> : undefined}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleTestPrint(printer)}
                        color="success"
                        disabled={testingPrinterId === printer.id || printer.connectionType === 'bluetooth'}
                        title={printer.connectionType === 'bluetooth' ? 'Bluetoothはアプリでテスト' : 'テスト印刷'}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(printer)}
                        color="primary"
                        title="編集"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeleteTarget(printer);
                          setOpenDeleteDialog(true);
                        }}
                        color="error"
                        title="削除"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 編集/追加ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPrinter ? 'プリンター編集' : 'プリンター追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="プリンター名"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              placeholder="例: キッチンプリンター1"
            />

            <FormControl fullWidth required>
              <InputLabel>プリンタータイプ</InputLabel>
              <Select
                value={formData.type}
                label="プリンタータイプ"
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              >
                <MenuItem value="kitchen">キッチン（調理場）</MenuItem>
                <MenuItem value="bar">バー（ドリンク）</MenuItem>
                <MenuItem value="receipt">レシート（会計）</MenuItem>
                <MenuItem value="other">その他</MenuItem>
              </Select>
            </FormControl>

            {/* 接続タイプ選択 */}
            <FormControl fullWidth required>
              <InputLabel>接続タイプ</InputLabel>
              <Select
                value={formData.connectionType}
                label="接続タイプ"
                onChange={(e) => {
                  const connectionType = e.target.value as PrinterConnectionType;
                  // 接続タイプに応じてデフォルトのモデルを設定
                  let defaultModel = formData.model;
                  if (connectionType === 'bluetooth' && !formData.model.startsWith('Star-') && !formData.model.startsWith('Epson-')) {
                    defaultModel = 'Star-mPOP';
                  } else if (connectionType === 'network' && formData.model.startsWith('Star-')) {
                    defaultModel = 'SII-RP-F10';
                  }
                  setFormData({
                    ...formData,
                    connectionType,
                    model: defaultModel,
                    hasDrawer: connectionType === 'bluetooth' && defaultModel === 'Star-mPOP',
                  });
                }}
              >
                <MenuItem value="network">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WifiIcon fontSize="small" />
                    ネットワーク（Wi-Fi/LAN）
                  </Box>
                </MenuItem>
                <MenuItem value="bluetooth">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BluetoothIcon fontSize="small" />
                    Bluetooth
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>プリンター機種</InputLabel>
              <Select
                value={formData.model}
                label="プリンター機種"
                onChange={(e) => {
                  const model = e.target.value;
                  setFormData({
                    ...formData,
                    model,
                    // mPOPはドロワー内蔵
                    hasDrawer: model === 'Star-mPOP',
                  });
                }}
              >
                {formData.connectionType === 'network' && [
                  // ネットワーク対応モデル
                  <MenuItem key="SII-RP-F10" value="SII-RP-F10">SII RP-F10（推奨）</MenuItem>,
                  <MenuItem key="SII-RP-E11" value="SII-RP-E11">SII RP-E11</MenuItem>,
                  <MenuItem key="SII-RP-D10" value="SII-RP-D10">SII RP-D10</MenuItem>,
                  <MenuItem key="SII-RP-E10" value="SII-RP-E10">SII RP-E10</MenuItem>,
                  <MenuItem key="Star-TSP100" value="Star-TSP100">Star TSP100シリーズ</MenuItem>,
                  <MenuItem key="Epson-TM-T88" value="Epson-TM-T88">EPSON TM-T88シリーズ</MenuItem>,
                  <MenuItem key="Epson-TM-m30" value="Epson-TM-m30">EPSON TM-m30シリーズ</MenuItem>,
                  <MenuItem key="Other" value="Other">その他</MenuItem>,
                ]}
                {formData.connectionType === 'bluetooth' && [
                  // Bluetooth対応モデル
                  <MenuItem key="Star-mPOP" value="Star-mPOP">Star mPOP（ドロワー内蔵）</MenuItem>,
                  <MenuItem key="Star-TSP100-BT" value="Star-TSP100">Star TSP100シリーズ</MenuItem>,
                  <MenuItem key="Epson-TM-m30-BT" value="Epson-TM-m30">EPSON TM-m30シリーズ</MenuItem>,
                  <MenuItem key="Other-BT" value="Other">その他</MenuItem>,
                ]}
              </Select>
            </FormControl>

            {/* ネットワーク接続設定 */}
            {formData.connectionType === 'network' && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <TextField
                      label="IPアドレス"
                      value={formData.ipAddress}
                      onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                      required
                      fullWidth
                      placeholder="192.168.1.100"
                      helperText="プリンターのIPアドレスを入力"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="ポート"
                      type="number"
                      value={formData.port}
                      onChange={(e) => setFormData({ ...formData, port: Number(e.target.value) })}
                      required
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Alert severity="info">
                  <Typography variant="caption" display="block" gutterBottom>
                    <strong>Wi-Fi接続の手順:</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    1. プリンター本体のメニューから「ネットワーク設定」→「Wi-Fi設定」を選択
                  </Typography>
                  <Typography variant="caption" display="block">
                    2. 店舗のWi-FiネットワークのSSIDを選択してパスワードを入力
                  </Typography>
                  <Typography variant="caption" display="block">
                    3. 接続後、「ネットワーク情報」からIPアドレスを確認
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    ※ ルーターの管理画面からもIPアドレスを確認できます（推奨：固定IPアドレスの設定）
                  </Typography>
                  <Typography variant="caption" display="block">
                    ※ 通常、ポートは9100を使用します
                  </Typography>
                </Alert>
              </>
            )}

            {/* Bluetooth接続設定 */}
            {formData.connectionType === 'bluetooth' && (
              <>
                <TextField
                  label="デバイス名"
                  value={formData.bluetoothName}
                  onChange={(e) => setFormData({ ...formData, bluetoothName: e.target.value })}
                  fullWidth
                  placeholder="例: mPOP"
                  helperText="Bluetoothデバイス名（ペアリング時に表示される名前）"
                />

                <TextField
                  label="MACアドレス（任意）"
                  value={formData.bluetoothAddress}
                  onChange={(e) => setFormData({ ...formData, bluetoothAddress: e.target.value })}
                  fullWidth
                  placeholder="例: 00:11:22:33:44:55"
                  helperText="MACアドレス（わかる場合のみ入力）"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.hasDrawer}
                      onChange={(e) => setFormData({ ...formData, hasDrawer: e.target.checked })}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DrawerIcon fontSize="small" />
                      キャッシュドロワー内蔵
                    </Box>
                  }
                />

                <Alert severity="info">
                  <Typography variant="caption" display="block" gutterBottom>
                    <strong>Bluetooth接続の手順:</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    1. プリンターの電源を入れ、Bluetoothを有効化
                  </Typography>
                  <Typography variant="caption" display="block">
                    2. iPad/Android端末の設定アプリでBluetoothペアリング
                  </Typography>
                  <Typography variant="caption" display="block">
                    3. アプリでデバイス名を入力して保存
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    ※ Star mPOPは本体下部にキャッシュドロワーが内蔵されています
                  </Typography>
                  <Typography variant="caption" display="block">
                    ※ Bluetooth接続はスタッフアプリ（iOS/Android）でのみ使用できます
                  </Typography>
                </Alert>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPrinter ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>プリンターを削除</DialogTitle>
        <DialogContent>
          <Typography>
            プリンター「{deleteTarget?.name}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            ※ このプリンターに紐付けられた商品の印刷設定に影響があります。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Printers;
